import { evidenceCoverageFormSchema } from "./evidence-coverage";
import { eligibleComplianceReviewersSchema } from "@/generated/compliance-control-contract";
/**
 * Compliance reports — queries + mutations.
 *
 * Backed by internal/tenant/compliance.AdminHandler, mounted at
 * /api/v1/tenants/{tenant_id}/compliance/reports via
 * WithPathTenant.
 *
 * Config + queue CRUD is live; the worker that drains queued
 * rows and materialises CSV/JSONL exports (plus signed download
 * URLs) is a follow-up subsystem — see the TODO in
 * internal/tenant/compliance/service.go.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiFetch, rewritePathForHost } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  auditorAccessLogListSchema,
  auditorTokenSchema,
  auditorTokenListSchema,
  transparencyWitnessListSchema,
  transparencyWitnessSchema,
  changedControlsListSchema,
  controlDetailSchema,
  frameworkSelectionSchema,
  type FrameworkSelection,
  scopeRequestListSchema,
  dashboardSummarySchema,
  evidenceArtefactSchema,
  evidenceLinkCreateSchema,
  evidenceListResponseSchema,
  evidenceMetadataUpdateSchema,
  evidenceSensitivitySchema,
  type EvidenceLinkCreate,
  type EvidenceMetadataUpdate,
  type EvidenceSensitivity,
  findingsPageSchema,
  frameworkDetailSchema,
  questionnaireResponseSchema,
  readinessReportSchema,
  refreshStatusSchema,
  reportSchema,
  reportsListResponseSchema,
  trendSeriesSchema,
  type AuditorAccessLogList,
  type AuditorToken,
  type AuditorTokenList,
  type ChangedControlsList,
  type ControlDetail,
  type DashboardSummary,
  type EvidenceArtefact,
  type EvidenceListResponse,
  type FindingsPage,
  type FindingsSort,
  type FrameworkDetail,
  type IssueAuditorTokenInput,
  type QuestionnaireFormat,
  type QuestionnaireResponse,
  type ReadinessReport,
  type RefreshStatus,
  type Report,
  type ReportsListResponse,
  type RequestReportInput,
  type TrendSeries,
  controlHistoryResponseSchema,
  type ControlHistoryEntry,
  type SetOwnershipInput,
  type AttestControlInput,
  counterSignatureSchema,
  type CounterSignature,
  mandateDocumentSchema,
  type MandateDocument,
  mandateRuleSetsResponseSchema,
  type MandateRuleSet,
  attestationRequestListSchema,
  pendingCloseListSchema,
  periodCloseListSchema,
  type TransparencyWitness,
  type TransparencyWitnessList,
  type RegisterWitnessInput,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/compliance/reports`;
}

export async function fetchReports(
  tenantID: string,
): Promise<ReportsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return reportsListResponseSchema.parse(raw);
}

export function useReportsQuery(tenantID: string) {
  return useQuery<ReportsListResponse, ApiError>({
    queryKey: queryKeys.compliance.reports(tenantID),
    queryFn: () => fetchReports(tenantID),
    enabled: tenantID.length > 0,
    // 10s ONLY while a run is in flight. A finished report is immutable:
    // its rows, its bytes and its hash cannot change, so polling the list
    // after the queue drains asks the server the same question forever
    // and gets the same answer. The one thing that does change later — a
    // counter-signature — arrives through the mutation that adds it,
    // which invalidates this key.
    refetchInterval: (query) => {
      const inFlight = query.state.data?.data.some(
        (r) => r.status === "queued" || r.status === "running",
      );
      return inFlight ? 10_000 : false;
    },
  });
}

/**
 * The rule sets the mandate readiness report can be run through. Static
 * on the server, so it is fetched once and kept.
 */
export async function fetchMandateRuleSets(tenantID: string): Promise<MandateRuleSet[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID).replace(/\/reports$/, "")}/mandate-rule-sets`);
  return mandateRuleSetsResponseSchema.parse(raw).data;
}

export function useMandateRuleSetsQuery(tenantID: string) {
  return useQuery<MandateRuleSet[], ApiError>({
    queryKey: queryKeys.compliance.mandateRuleSets(tenantID),
    queryFn: () => fetchMandateRuleSets(tenantID),
    enabled: tenantID.length > 0,
    staleTime: Infinity,
  });
}

/**
 * A document run, read back from the stored export — the same bytes the
 * content hash and any counter-signature cover. Never polled: a finished
 * run is immutable.
 */
export async function fetchReportDocument(tenantID: string, reportID: string): Promise<MandateDocument> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(reportID)}/document`);
  return mandateDocumentSchema.parse(raw);
}

export function useReportDocumentQuery(tenantID: string, reportID: string | undefined) {
  return useQuery<MandateDocument, ApiError>({
    queryKey: queryKeys.compliance.reportDocument(tenantID, reportID ?? ""),
    queryFn: () => fetchReportDocument(tenantID, reportID ?? ""),
    enabled: tenantID.length > 0 && (reportID ?? "").length > 0,
    staleTime: Infinity,
  });
}

export function useRequestReportMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Report, ApiError, RequestReportInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => reportSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.all(tenantID) });
    },
  });
}

/**
 * getDownloadUrl asks the backend to mint a fresh short-lived
 * signed URL for a ready report. The SPA calls this only when
 * the user clicks "Download" — the list response never carries
 * a download URL so a 500-row inbox does not trigger 500 HMAC
 * mints. The returned URL expires in ~10 minutes server-side.
 */
export async function getDownloadUrl(
  tenantID: string,
  reportID: string,
): Promise<string> {
  const raw = await apiFetch<{ download_url: string }>(
    `${base(tenantID)}/${encodeURIComponent(reportID)}/download`,
  );
  return raw.download_url;
}

// ── Catalog-backed dashboard / framework / control queries ───────────────
//
// Hit the substrate (compliance_control_catalog +
// compliance_control_assessment) via the posture admin handler.

function postureV2Base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/compliance`;
}

export async function fetchComplianceDashboard(
  tenantID: string,
): Promise<DashboardSummary> {
  const raw = await apiFetch<unknown>(`${postureV2Base(tenantID)}/dashboard`);
  return dashboardSummarySchema.parse(raw);
}

export function useComplianceDashboardQuery(tenantID: string) {
  const queryClient = useQueryClient();
  return useQuery<DashboardSummary, ApiError>({
    queryKey: ["compliance-v2", "dashboard", tenantID],
    queryFn: async () => {
      const data = await fetchComplianceDashboard(tenantID);
      // Seed the refresh-status cache from the embedded field so
      // the RefreshBar renders immediately on first paint without
      // a second GET. Subsequent polling continues against the
      // dedicated endpoint.
      if (data.refresh_status) {
        queryClient.setQueryData(
          ["compliance-v2", "refresh-status", tenantID],
          data.refresh_status,
        );
      }
      return data;
    },
    enabled: tenantID.length > 0,
    // v2 refresh model: posture data changes once per 24 hours by
    // default. Polling the dashboard endpoint on a fast interval
    // would just hammer the projection. Instead we rely on the
    // refresh-status query (below) — when an in-flight refresh
    // completes, that hook invalidates this query so the operator
    // sees the new numbers without reload.
    staleTime: 60_000,
  });
}

// ── Findings datatable (paginated) ───────────────────────────────
//
// Backend: GET /compliance/findings (offset cursor, search,
// sortable columns). The SPA's findings table calls
// useFindingsInfiniteQuery — TanStack accumulates the pages
// for us; the page component flattens to one row array and
// fetches the next page via fetchNextPage() when an
// IntersectionObserver sees the sentinel.

export interface FindingsQueryFilter {
  search: string;
  sort: FindingsSort;
}

const FINDINGS_PAGE_SIZE = 50;

export function useFindingsInfiniteQuery(
  tenantID: string,
  filter: FindingsQueryFilter,
) {
  return useInfiniteQuery({
    queryKey: [
      "compliance-v2",
      "findings",
      tenantID,
      filter.search.trim(),
      filter.sort,
    ] as const,
    queryFn: async ({ pageParam }): Promise<FindingsPage> => {
      const qs = new URLSearchParams();
      if (filter.search.trim()) qs.set("search", filter.search.trim());
      qs.set("sort", filter.sort);
      // pageParam is the opaque token from the previous page's
      // next_cursor (empty string = first page).
      if (pageParam) qs.set("cursor", pageParam);
      qs.set("limit", String(FINDINGS_PAGE_SIZE));
      const raw = await apiFetch<unknown>(
        `${postureV2Base(tenantID)}/findings?${qs.toString()}`,
      );
      return findingsPageSchema.parse(raw);
    },
    initialPageParam: "" as string,
    getNextPageParam: (last: FindingsPage): string | undefined =>
      last.next_cursor ?? undefined,
    enabled: tenantID.length > 0,
    // Findings are invalidated by the refresh-status hook when a
    // posture refresh completes, so this staleTime only governs
    // the no-refresh-running case.
    staleTime: 60_000,
  });
}

// ── v2 refresh queue ─────────────────────────────────────────────
//
// Three pieces:
//   1. fetchRefreshStatus / useRefreshStatusQuery — polls the queue
//      row. Polling cadence is conditional: every 3s while a refresh
//      is in flight (so the UI shows progress), otherwise off.
//   2. useTriggerRefreshMutation — POST /compliance/refresh.
//   3. The dashboard query is invalidated when in_flight transitions
//      from true→false so the new assessments land without manual
//      reload.

export async function fetchRefreshStatus(tenantID: string): Promise<RefreshStatus> {
  const raw = await apiFetch<unknown>(`${postureV2Base(tenantID)}/refresh-status`);
  return refreshStatusSchema.parse(raw);
}

export function useRefreshStatusQuery(tenantID: string) {
  const queryClient = useQueryClient();
  return useQuery<RefreshStatus, ApiError>({
    queryKey: ["compliance-v2", "refresh-status", tenantID],
    queryFn: async () => {
      const next = await fetchRefreshStatus(tenantID);
      // When a previously-in-flight refresh has completed, invalidate
      // the dashboard so the new posture lands automatically. Cheap
      // because the cache key only changes when in_flight flips.
      const prev = queryClient.getQueryData<RefreshStatus>([
        "compliance-v2",
        "refresh-status",
        tenantID,
      ]);
      if (prev?.in_flight && !next.in_flight) {
        queryClient.invalidateQueries({
          queryKey: ["compliance-v2", "dashboard", tenantID],
        });
        // Findings derive from the same assessed-controls data
        // the dashboard reads, so they go stale on the same
        // signal. Prefix-match invalidation drops every (search,
        // sort) variant currently in cache, forcing the
        // FindingsTable to refetch from page 1 with the new
        // posture rather than letting it show pre-refresh rows
        // for up to staleTime.
        queryClient.invalidateQueries({
          queryKey: ["compliance-v2", "findings", tenantID],
        });
      }
      return next;
    },
    enabled: tenantID.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data;
      // Poll every 3s while a refresh is running; otherwise let it
      // re-fetch only on tab focus or explicit invalidation. Slow-
      // changing data does not need a constant heartbeat.
      return status?.in_flight ? 3_000 : false;
    },
    // Refresh status is small + cheap; revalidate on tab focus so a
    // sleeping browser shows live state on wake.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useTriggerRefreshMutation(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation<RefreshStatus, ApiError, void>({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>(
        `${postureV2Base(tenantID)}/refresh`,
        { method: "POST" },
      );
      return refreshStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      // Seed the cache so the refresh-status hook starts polling
      // without an extra GET roundtrip.
      queryClient.setQueryData(
        ["compliance-v2", "refresh-status", tenantID],
        status,
      );
    },
  });
}

// useTriggerControlRefreshMutation — per-control "Run check" button.
//
// POST /compliance/controls/{catalogId}/refresh enqueues an urgent-
// priority job; the worker drains it ahead of any tenant-wide sweep.
// On success we POLL the control detail query (every 750ms, cap 12s)
// until `assessed_at` advances past the click time, then invalidate
// the dashboard so summary numbers refresh. Polling beats a single
// timed invalidate because the worker latency is variable — a slow
// evaluator + claim contention can run >1.5s. The poll cap is the
// graceful upper bound; if the worker still hasn't completed,
// further behavior is whatever the next user navigation triggers.
export function useTriggerControlRefreshMutation(
  tenantID: string,
  catalogID: string,
) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await apiFetch<unknown>(
        `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogID)}/refresh`,
        { method: "POST" },
      );
    },
    onSuccess: () => {
      const clickedAt = Date.now();
      const startedKnownAssessedAt = readAssessedAt(
        queryClient.getQueryData<ControlDetail>([
          "compliance-v2",
          "control",
          tenantID,
          catalogID,
        ]),
      );

      const POLL_INTERVAL_MS = 750;
      const POLL_CAP_MS = 12_000;
      const startedAt = Date.now();

      const poll = async () => {
        await queryClient.invalidateQueries({
          queryKey: ["compliance-v2", "control", tenantID, catalogID],
        });
        const fresh = queryClient.getQueryData<ControlDetail>([
          "compliance-v2",
          "control",
          tenantID,
          catalogID,
        ]);
        const freshAssessedAt = readAssessedAt(fresh);
        const advanced =
          freshAssessedAt != null &&
          (startedKnownAssessedAt == null ||
            freshAssessedAt > startedKnownAssessedAt) &&
          freshAssessedAt >= clickedAt - 1000; // tolerate small clock skew

        if (advanced || Date.now() - startedAt > POLL_CAP_MS) {
          // Settled (or capped). Refresh the dashboard so summary
          // numbers track the new verdict.
          queryClient.invalidateQueries({
            queryKey: ["compliance-v2", "dashboard", tenantID],
          });
          // And the control's own record. A re-run writes a snapshot, so
          // the timeline directly below the verdict tiles has a new entry
          // — and it is deliberately never polled, so nothing else was
          // going to fetch it. The operator saw the verdict change above
          // a record that did not mention it.
          queryClient.invalidateQueries({
            queryKey: ["compliance-v2", "control-history", tenantID, catalogID],
          });
          return;
        }
        window.setTimeout(poll, POLL_INTERVAL_MS);
      };
      window.setTimeout(poll, POLL_INTERVAL_MS);
    },
  });
}

function readAssessedAt(detail: ControlDetail | undefined): number | undefined {
  const raw = detail?.assessed_at;
  if (!raw) return undefined;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? undefined : t;
}

export async function fetchFrameworkDetail(
  tenantID: string,
  frameworkId: string,
  frameworkVersion: string,
): Promise<FrameworkDetail> {
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/frameworks/${encodeURIComponent(
      frameworkId,
    )}/${encodeURIComponent(frameworkVersion)}`,
  );
  return frameworkDetailSchema.parse(raw);
}

export function useFrameworkDetailQuery(
  tenantID: string,
  frameworkId: string,
  frameworkVersion: string,
) {
  return useQuery<FrameworkDetail, ApiError>({
    queryKey: ["compliance-v2", "framework", tenantID, frameworkId, frameworkVersion],
    queryFn: () => fetchFrameworkDetail(tenantID, frameworkId, frameworkVersion),
    enabled:
      tenantID.length > 0 &&
      frameworkId.length > 0 &&
      frameworkVersion.length > 0,
    staleTime: 60_000,
  });
}

export function useFrameworkWorkQueries(tenantID: string, frameworks: FrameworkRef[]) {
  return useQueries({ queries: frameworks.map((f) => ({
    queryKey: ["compliance-v2", "framework", tenantID, f.framework_id, f.framework_version],
    queryFn: () => fetchFrameworkDetail(tenantID, f.framework_id, f.framework_version),
    staleTime: 60_000,
  })) });
}

export async function fetchControlDetail(
  tenantID: string,
  catalogId: string,
): Promise<ControlDetail> {
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogId)}`,
  );
  return controlDetailSchema.parse(raw);
}

export function useControlDetailQuery(tenantID: string, catalogId: string) {
  return useQuery<ControlDetail, ApiError>({
    queryKey: ["compliance-v2", "control", tenantID, catalogId],
    queryFn: () => fetchControlDetail(tenantID, catalogId),
    enabled: tenantID.length > 0 && catalogId.length > 0,
    staleTime: 60_000,
  });
}

/**
 * One control's own record — every snapshot, newest first, with hashes.
 *
 * NEVER POLLED. A snapshot is immutable and append-only; polling asks the
 * server for a past that cannot have changed. New entries arrive when the
 * thing that writes them does — a refresh, an attestation, a treatment —
 * and each of those invalidates this key through its own mutation.
 */
export function useControlHistoryQuery(tenantID: string, catalogId: string) {
  return useQuery<ControlHistoryEntry[], ApiError>({
    queryKey: ["compliance-v2", "control-history", tenantID, catalogId],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogId)}/history`,
      );
      return controlHistoryResponseSchema.parse(raw).data ?? [];
    },
    enabled: tenantID.length > 0 && catalogId.length > 0,
    staleTime: 60_000,
  });
}

/**
 * A second person's review of a report run.
 *
 * THIS IS WHAT TURNS A LISTING INTO A REVIEW. An access listing nobody
 * reviewed is a query result; the same listing with a named reviewer's
 * passkey over its hash is user-access-review evidence, which is the
 * thing an auditor asked for. The landing page raises un-counter-signed
 * runs as a finding, and this is where that finding is answered.
 *
 * The server refuses the person who ran it, an already-signed run, an
 * unfinished one, and an API-key principal.
 */
export function useCounterSignReportMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<CounterSignature, ApiError, string>({
    mutationFn: (reportID) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(reportID)}/counter-sign`,
          { method: "POST" },
        ).then((raw) => counterSignatureSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.reports(tenantID) });
    },
  });
}

/**
 * OFFERING an attestation for review.
 *
 * It does not attest anything. The statement goes to a named colleague
 * and the control reads exactly as it did until they accept it from
 * their own session — a statement nobody has reviewed is not evidence,
 * and rendering it as one is the claim the two-session split removed.
 *
 * Step-up gated by the permission itself (compliance.attest carries
 * step_up_required), and the server additionally refuses an API-key
 * principal: an attestation is a person's signature, and a key is not a
 * person.
 */
export function useAttestControlMutation(tenantID: string, catalogId: string) {
  const qc = useQueryClient();
  return useMutation<ControlDetail, ApiError, AttestControlInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogId)}/attest`,
          { method: "POST", body: input },
        ).then((raw) => controlDetailSchema.parse(raw)),
      ),
    onSuccess: () => {
      // The OFFER changes nothing on the register — deliberately — so
      // blowing the whole compliance cache would be a refetch that
      // returns identical data. What did change is the reviewer's queue.
      void qc.invalidateQueries({
        queryKey: ["compliance-v2", "attestation-requests", tenantID],
      });
    },
  });
}

/**
 * Naming who answers for a control.
 *
 * Step-up gated like every other write on this surface: moving
 * accountability from one person to another changes who answers for the
 * gap, which is the same kind of act as granting a treatment.
 */
export function useSetOwnershipMutation(tenantID: string, catalogId: string) {
  const qc = useQueryClient();
  return useMutation<ControlDetail, ApiError, SetOwnershipInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogId)}/ownership`,
          { method: "PUT", body: input },
        ).then((raw) => controlDetailSchema.parse(raw)),
      ),
    onSuccess: () => {
      // The register shows the owner too, so the framework and dashboard
      // reads are stale the moment this lands.
      qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

/**
 * useSetOverrideMutation is GONE, with the PUT /override route it called.
 *
 * Its only accepted status was not_applicable — the largest single act on
 * this surface, removing a control from the denominator entirely — and it
 * took one person and a scope note nobody else read. Marking a control
 * not applicable now opens a request a colleague decides; see
 * useRequestScopeExclusionMutation below.
 *
 * Exceptions were never this endpoint: they have always been PUT
 * /exception with a named approver.
 */


/**
 * Clearing a treatment. compliance.scope, no step-up: the act is recorded
 * in Audit and the server does not demand a ceremony for it, so the
 * client does not pretend one is needed.
 */
export function useClearOverrideMutation(tenantID: string, catalogId: string) {
  const qc = useQueryClient();
  return useMutation<ControlDetail, ApiError, void>({
    mutationFn: () =>
      apiFetch<unknown>(
        `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogId)}/override`,
        { method: "DELETE" },
      ).then((raw) => controlDetailSchema.parse(raw)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

// ── Phase I: questionnaire auto-fill ───────────────────────────────────

export async function fetchQuestionnaire(
  tenantID: string,
  format: QuestionnaireFormat,
): Promise<QuestionnaireResponse> {
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/questionnaire?format=${format}`,
  );
  return questionnaireResponseSchema.parse(raw);
}

// ── Phase H: audit-readiness ───────────────────────────────────────────

export async function fetchReadinessReport(
  tenantID: string,
  frameworkId: string,
  frameworkVersion: string,
): Promise<ReadinessReport> {
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/frameworks/${encodeURIComponent(
      frameworkId,
    )}/${encodeURIComponent(frameworkVersion)}/readiness`,
  );
  return readinessReportSchema.parse(raw);
}

export function useReadinessReportQuery(
  tenantID: string,
  frameworkId: string,
  frameworkVersion: string,
) {
  return useQuery<ReadinessReport, ApiError>({
    queryKey: [
      "compliance-v2",
      "readiness",
      tenantID,
      frameworkId,
      frameworkVersion,
    ],
    queryFn: () => fetchReadinessReport(tenantID, frameworkId, frameworkVersion),
    enabled:
      tenantID.length > 0 &&
      frameworkId.length > 0 &&
      frameworkVersion.length > 0,
    staleTime: 60_000,
  });
}

// ── Phase G: trend + what-changed feed ─────────────────────────────────

export async function fetchComplianceTrend(
  tenantID: string,
  days = 90,
): Promise<TrendSeries> {
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/trend?days=${days}`,
  );
  return trendSeriesSchema.parse(raw);
}

export function useComplianceTrendQuery(tenantID: string, days = 90) {
  return useQuery<TrendSeries, ApiError>({
    queryKey: ["compliance-v2", "trend", tenantID, days],
    queryFn: () => fetchComplianceTrend(tenantID, days),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export async function fetchComplianceChanges(
  tenantID: string,
  since?: Date,
  limit = 50,
): Promise<ChangedControlsList> {
  const sinceStr =
    since?.toISOString() ??
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const raw = await apiFetch<unknown>(
    `${postureV2Base(tenantID)}/changes?since=${encodeURIComponent(sinceStr)}&limit=${limit}`,
  );
  return changedControlsListSchema.parse(raw);
}

export function useComplianceChangesQuery(
  tenantID: string,
  windowDays = 7,
  limit = 50,
) {
  return useQuery<ChangedControlsList, ApiError>({
    queryKey: ["compliance-v2", "changes", tenantID, windowDays, limit],
    queryFn: () =>
      fetchComplianceChanges(
        tenantID,
        new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000),
        limit,
      ),
    enabled: tenantID.length > 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}

// ── Phase E: auditor portal token management ──────────────────────────

// ── transparency witnesses ───────────────────────────────────────────
//
// The register of parties holding copies of this deployment's tree
// heads. Read on the Signed records tab; written under compliance.manage
// with the step-up that permission carries, because choosing who may
// hold the history is a security decision.

function witnessesBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/compliance/witnesses`;
}

export function useTransparencyWitnessesQuery(tenantID: string) {
  return useQuery<TransparencyWitnessList, ApiError>({
    queryKey: queryKeys.transparencyWitnesses.all(tenantID),
    queryFn: async () =>
      transparencyWitnessListSchema.parse(await apiFetch<unknown>(witnessesBase(tenantID))),
    enabled: tenantID.length > 0,
    refetchOnWindowFocus: false,
  });
}

export function useRegisterWitnessMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<TransparencyWitness, ApiError, RegisterWitnessInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(witnessesBase(tenantID), { method: "POST", body: input }).then((raw) =>
          transparencyWitnessSchema.parse(raw),
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.transparencyWitnesses.all(tenantID) });
    },
  });
}

export function useRevokeWitnessMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<TransparencyWitness, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<unknown>(`${witnessesBase(tenantID)}/${encodeURIComponent(id)}`, {
          method: "DELETE",
        }).then((raw) => transparencyWitnessSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.transparencyWitnesses.all(tenantID) });
    },
  });
}

function auditorTokensBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/compliance/auditor-tokens`;
}

export async function fetchAuditorTokens(
  tenantID: string,
): Promise<AuditorTokenList> {
  const raw = await apiFetch<unknown>(auditorTokensBase(tenantID));
  return auditorTokenListSchema.parse(raw);
}

export function useAuditorTokensQuery(tenantID: string) {
  return useQuery<AuditorTokenList, ApiError>({
    queryKey: queryKeys.auditorTokens.all(tenantID),
    queryFn: () => fetchAuditorTokens(tenantID),
    enabled: tenantID.length > 0,
    refetchOnWindowFocus: false,
  });
}

export function useIssueAuditorTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AuditorToken, ApiError, IssueAuditorTokenInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(auditorTokensBase(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => auditorTokenSchema.parse(raw)),
      ),
    // Token plaintext is in the resolved promise value ONLY. Never
    // setQueryData here — that would write the secret into the
    // observable cache.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auditorTokens.all(tenantID) });
    },
  });
}

export function useRevokeAuditorTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (tokenID) =>
      withStepUp(() =>
        apiFetch<void>(
          `${auditorTokensBase(tenantID)}/${encodeURIComponent(tokenID)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auditorTokens.all(tenantID) });
      qc.invalidateQueries({ queryKey: ["compliance-v2", "auditor-access-log", tenantID] });
    },
  });
}

/**
 * Name (or clear) the accountable owner of an auditor portal token.
 *
 * The credential a tenant hands an EXTERNAL auditor, so "who answers for this
 * engagement" is a question a reviewer will actually ask about it after the fact —
 * which is also why the row stays reassignable after revocation.
 *
 * queryKeys.auditorTokens.all is one of the six heads wireCensusInvalidation
 * watches, so this one call refreshes the census roster's Owner column too.
 */
export function useSetAuditorTokenOwnerMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { tokenID: string; ownerAdminID: string | null }>({
    mutationFn: ({ tokenID, ownerAdminID }) =>
      withStepUp(() =>
        apiFetch<void>(
          `${auditorTokensBase(tenantID)}/${encodeURIComponent(tokenID)}/owner`,
          {
            method: "PUT",
            // NOT pre-stringified: apiFetch serializes `body` itself. Doing it
            // here sent the JSON *string* as the body and every save 400'd.
            //
            // An explicit null CLEARS the owner — the server distinguishes it
            // from an absent field, and unowned is a chosen state.
            body: { owner_admin_id: ownerAdminID },
          },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auditorTokens.all(tenantID) });
    },
  });
}

export async function fetchAuditorAccessLog(
  tenantID: string,
): Promise<AuditorAccessLogList> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/compliance/auditor-access-log?limit=200`,
  );
  return auditorAccessLogListSchema.parse(raw);
}

export function useAuditorAccessLogQuery(tenantID: string) {
  return useQuery<AuditorAccessLogList, ApiError>({
    queryKey: ["compliance-v2", "auditor-access-log", tenantID],
    queryFn: () => fetchAuditorAccessLog(tenantID),
    enabled: tenantID.length > 0,
    // Refresh every 30s so a tenant admin watching for an
    // auditor session sees activity within half a minute.
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useCancelReportMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (reportId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(reportId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.all(tenantID) });
    },
  });
}

// ── Evidence locker ───────────────────────────────────────────────────────
//
// Operator-uploaded artefacts attached to compliance controls.
// The list endpoints are read-gated; upload / link / delete go
// through compliance.manage with step-up.

export async function fetchEvidenceForControl(
  tenantID: string,
  catalogID: string,
): Promise<EvidenceListResponse> {
  const url = `${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogID)}/evidence`;
  const raw = await apiFetch<unknown>(url);
  return evidenceListResponseSchema.parse(raw);
}

export function useControlEvidenceQuery(tenantID: string, catalogID: string) {
  return useQuery<EvidenceListResponse, ApiError>({
    queryKey: ["compliance-v2", "evidence", tenantID, catalogID],
    queryFn: () => fetchEvidenceForControl(tenantID, catalogID),
    enabled: tenantID.length > 0 && catalogID.length > 0,
    staleTime: 30_000,
  });
}

export interface UploadEvidenceInput {
  file: File;
  title?: string;
  description?: string;
  sensitivity?: "public_safe" | "auditor_only" | "internal_only";
  retentionUntil?: string;
  /** The period the document is evidence for, as YYYY-MM-DD. */
  coversFrom?: string;
  coversTo?: string;
  catalogID?: string; // when set, auto-links to that control after upload
}

// A mutation's error is an ApiError; input the form should have refused
// surfaces as the server would have refused it, not as a raw ZodError.
function invalidEvidenceInput(title: string, issues: unknown): ApiError {
  return new ApiError(422, { title, issues }, null, title);
}

export function useUploadEvidenceMutation(
  tenantID: string,
  catalogID?: string,
) {
  const qc = useQueryClient();
  return useMutation<EvidenceArtefact, ApiError, UploadEvidenceInput>({
    mutationFn: async (input) => {
      const coverage = evidenceCoverageFormSchema.safeParse({ coversFrom: input.coversFrom ?? "", coversTo: input.coversTo ?? "" });
      if (!coverage.success) throw invalidEvidenceInput("Invalid period of coverage", coverage.error.issues);
      const fd = new FormData();
      fd.append("file", input.file);
      if (input.title) fd.append("title", input.title);
      if (input.description) fd.append("description", input.description);
      if (input.sensitivity) fd.append("sensitivity", input.sensitivity);
      if (input.retentionUntil) fd.append("retention_until", input.retentionUntil);
      if (input.coversFrom) fd.append("covers_from", input.coversFrom);
      if (input.coversTo) fd.append("covers_to", input.coversTo);
      const link = input.catalogID ?? catalogID;
      const url = link
        ? `${postureV2Base(tenantID)}/evidence?catalog_id=${encodeURIComponent(link)}`
        : `${postureV2Base(tenantID)}/evidence`;
      const raw = await withStepUp(() =>
        apiFetch<unknown>(url, { method: "POST", body: fd }),
      );
      return evidenceArtefactSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence", tenantID] });
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence-tenant", tenantID] });
    },
  });
}

export function useUpdateEvidenceMetadataMutation(tenantID: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence", tenantID] });
    void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence-tenant", tenantID] });
  };
  return useMutation<EvidenceArtefact, ApiError, { evidenceID: string; metadata: EvidenceMetadataUpdate }>({
    mutationFn: async ({ evidenceID, metadata }) => {
      const parsed = evidenceMetadataUpdateSchema.safeParse(metadata);
      if (!parsed.success) throw invalidEvidenceInput("Invalid document details", parsed.error.issues);
      const body = parsed.data;
      const raw = await withStepUp(() => apiFetch<unknown>(
        `${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}`,
        { method: "PATCH", body },
      ));
      return evidenceArtefactSchema.parse(raw);
    },
    onSuccess: refresh,
    onError: (err) => { if (err.status === 409) refresh(); },
  });
}

export function useUnlinkEvidenceMutation(tenantID: string, catalogID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (evidenceID) =>
      withStepUp(() =>
        apiFetch<void>(
          `${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}/links/${encodeURIComponent(catalogID)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["compliance-v2", "evidence", tenantID, catalogID],
      });
    },
  });
}

// Every evidence list this tenant holds: each control's linked list and the
// tenant-wide locker. A document change can show on any of them.
function refreshEvidenceLists(qc: ReturnType<typeof useQueryClient>, tenantID: string) {
  void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence", tenantID] });
  void qc.invalidateQueries({ queryKey: ["compliance-v2", "evidence-tenant", tenantID] });
}

/**
 * Withdraw a document from the locker (DELETE …/evidence/{id}). compliance.manage
 * with step-up. Withdrawal is kept on record; frozen packets are unchanged.
 */
export function useWithdrawEvidenceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (evidenceID) =>
      withStepUp(() =>
        apiFetch<void>(`${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}`, { method: "DELETE" }),
      ),
    onSuccess: () => refreshEvidenceLists(qc, tenantID),
  });
}

/**
 * Link a locker document to a control (POST …/evidence/{id}/links).
 * compliance.manage with step-up; the control's record notes the change.
 */
export function useLinkEvidenceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { evidenceID: string; link: EvidenceLinkCreate }>({
    mutationFn: async ({ evidenceID, link }) => {
      const parsed = evidenceLinkCreateSchema.safeParse(link);
      if (!parsed.success) throw invalidEvidenceInput("Invalid control link", parsed.error.issues);
      await withStepUp(() =>
        apiFetch<void>(`${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}/links`, {
          method: "POST",
          body: parsed.data,
        }),
      );
    },
    onSuccess: () => refreshEvidenceLists(qc, tenantID),
  });
}

/**
 * Change who may see a document (POST …/evidence/{id}/sensitivity).
 * compliance.attest with step-up, from an interactive session: it decides
 * whether the document goes into an external auditor's packet.
 */
export function useReclassifyEvidenceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EvidenceArtefact, ApiError, { evidenceID: string; sensitivity: EvidenceSensitivity }>({
    mutationFn: async ({ evidenceID, sensitivity }) => {
      const body = evidenceSensitivitySchema.parse({ sensitivity });
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}/sensitivity`, {
          method: "POST",
          body,
        }),
      );
      return evidenceArtefactSchema.parse(raw);
    },
    onSuccess: () => refreshEvidenceLists(qc, tenantID),
    onError: (err) => { if (err.status === 409) refreshEvidenceLists(qc, tenantID); },
  });
}

// evidenceDownloadURL builds the download URL for a given
// artefact id. The browser navigates to it; the backend streams
// the bytes after re-checking sensitivity per request.
export function evidenceDownloadURL(tenantID: string, evidenceID: string): string {
  return rewritePathForHost(`${postureV2Base(tenantID)}/evidence/${encodeURIComponent(evidenceID)}/download`);
}

// Tenant-wide evidence locker list — drives the dashboard
// Evidence tab. Hits the same admin endpoint (no catalog_id) so
// the response is every artefact the tenant has uploaded.
export async function fetchTenantEvidence(tenantID: string): Promise<EvidenceListResponse> {
  const url = `${postureV2Base(tenantID)}/evidence`;
  const raw = await apiFetch<unknown>(url);
  return evidenceListResponseSchema.parse(raw);
}

export function useTenantEvidenceQuery(tenantID: string) {
  return useQuery<EvidenceListResponse, ApiError>({
    queryKey: ["compliance-v2", "evidence-tenant", tenantID],
    queryFn: () => fetchTenantEvidence(tenantID),
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

// ── Two-person scope exclusion ───────────────────────────────────────────
//
// Marking a control not applicable removes it from the denominator
// entirely. It used to be one person's click; it is now a request a
// colleague decides. The old single-person override endpoint is gone, so
// there is no path that skips the second person.



export async function fetchScopeRequests(tenantID: string) {
  const raw = await apiFetch(`${postureV2Base(tenantID)}/scope-requests`);
  return scopeRequestListSchema.parse(raw).data;
}

export function useScopeRequestsQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "scope-requests", tenantID],
    queryFn: () => fetchScopeRequests(tenantID),
    // A request waits on a person, not on a job. 30s matches the recovery
    // inbox, which is the same shape of wait.
    refetchInterval: 30_000,
  });
}

export function useRequestScopeExclusionMutation(tenantID: string, catalogID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      rationale: string;
      reviewer_admin_id: string;
      kind: "scope_exclusion" | "exception";
      compensating_control?: string;
      expires_at?: string;
    }) =>
      // compliance.scope, no step-up: opening a request is recorded in
      // Audit and decided by a second person; the server asks for no
      // ceremony here and the client mirrors that.
      apiFetch(`${postureV2Base(tenantID)}/controls/${encodeURIComponent(catalogID)}/scope-request`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "scope-requests", tenantID] });
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "control", tenantID, catalogID] });
    },
  });
}

/**
 * The register and the Statement of Applicability, as files.
 *
 * URLs rather than fetches: the browser navigates and the backend streams
 * with the right Content-Disposition. These four endpoints existed with
 * no console caller — including the SoA, which is the artefact an ISO
 * auditor asks for by name, on a page that had been rebuilt around the
 * register without linking it.
 */
/**
 * DOWNLOAD URLS GO THROUGH THE HOST REWRITER.
 *
 * apiFetch rewrites /api/v1/tenants/{id} to /api/tenant/v1 on any host that
 * is not platform., and these are consumed as raw <a href> — which skips
 * apiFetch entirely. Un-rewritten, every one of them 404s for an operator
 * working from their own tenant domain: the same defect class as the five
 * ceremonies that were mounted on one route tree, re-introduced in the panels
 * built to fix it. The census page calls the rewriter explicitly for exactly
 * this reason, with a comment saying it is load-bearing on a tenant host.
 */
export function controlRegisterURL(
  tenantID: string,
  format: "csv" | "xlsx",
  // A framework ref narrows the file to one instrument — the register an
  // operator hands the auditor engaged for that instrument. Without it
  // the file spans every in-scope framework, which is a disclosure
  // decision, so every surface that links this labels which form it is.
  framework?: { frameworkId: string; frameworkVersion: string },
): string {
  const scope = framework
    ? `?framework_id=${encodeURIComponent(framework.frameworkId)}` +
      `&framework_version=${encodeURIComponent(framework.frameworkVersion)}`
    : "";
  return rewritePathForHost(`${postureV2Base(tenantID)}/controls.${format}${scope}`);
}

export function applicabilityExtractURL(
  tenantID: string,
  frameworkID: string,
  frameworkVersion: string,
  format: "csv" | "xlsx",
): string {
  return rewritePathForHost(
    `${postureV2Base(tenantID)}/frameworks/${encodeURIComponent(frameworkID)}` +
    `/${encodeURIComponent(frameworkVersion)}/soa.${format}`
  );
}

// ── Attestations and closes waiting on a second person ───────────────
//
// The decide endpoints existed with no caller for a while, which made the
// ceremony unusable from the console: an operator could offer an
// attestation and nobody could accept it. An endpoint with no caller is
// the same defect as a caller with no endpoint — it just fails silently
// instead of loudly.

export function useAttestationRequestsQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "attestation-requests", tenantID],
    queryFn: async () => {
      const raw = await apiFetch(`${postureV2Base(tenantID)}/attestation-requests`);
      return attestationRequestListSchema.parse(raw).data ?? [];
    },
    // A request waits on a person, not a job — the same cadence as the
    // scope-request inbox, which is the same shape of wait.
    refetchInterval: 30_000,
  });
}

export function useDecideAttestationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; approve: boolean; note: string }) =>
      withStepUp(() =>
        apiFetch(
          `${postureV2Base(tenantID)}/attestation-requests/${encodeURIComponent(id)}/decide`,
          { method: "POST", body },
        ),
      ),
    onSuccess: () => {
      // Accepting one changes the control's verdict, its confidence and
      // every count above it.
      void qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

export function usePendingClosesQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "close-requests", tenantID],
    queryFn: async () => {
      const raw = await apiFetch(`${postureV2Base(tenantID)}/period/close-requests`);
      return pendingCloseListSchema.parse(raw).data ?? [];
    },
    refetchInterval: 30_000,
  });
}

/**
 * The closes that were signed.
 *
 * Immutable records — append-only by trigger — so no polling: a signed
 * close never changes, and re-closing writes a new row that a mutation
 * invalidation brings in.
 */
export function usePeriodClosesQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "period-closes", tenantID],
    queryFn: async () => {
      const raw = await apiFetch(`${postureV2Base(tenantID)}/period-closes`);
      return periodCloseListSchema.parse(raw).data ?? [];
    },
  });
}

/** The frozen packet of a signed close, as an auditor receives it. */
export function periodClosePacketURL(tenantID: string, closeID: string): string {
  return rewritePathForHost(`${postureV2Base(tenantID)}/period-closes/${encodeURIComponent(closeID)}/packet`);
}

/**
 * The frozen packet of a close that is still waiting on a decision.
 *
 * Without this the reviewer is shown a manifest hash and nothing to
 * compare it against — "the reviewer approves specific bytes rather than
 * an intention" is then true of the record and false of their
 * experience.
 */
export function pendingClosePacketURL(tenantID: string, requestID: string): string {
  return rewritePathForHost(`${postureV2Base(tenantID)}/period/close-requests/${encodeURIComponent(requestID)}/packet`);
}

/**
 * Offering a period close for review. The packet is built and frozen
 * here; the signature comes later, from the reviewer's own session.
 */
export function useOfferCloseMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string; reviewed_by_admin_id: string }) =>
      withStepUp(() =>
        apiFetch(`${postureV2Base(tenantID)}/period/close`, { method: "POST", body }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "close-requests", tenantID] });
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "period-closes", tenantID] });
    },
  });
}

/**
 * Taking back your own offer.
 *
 * One pending request per control is enforced by a unique index, so an
 * attestation aimed at the wrong control — or at a reviewer who has since
 * left — locked that control until the fourteen-day lapse. Three error
 * messages told operators to withdraw and there was no withdraw path; this is
 * it. Refusing and withdrawing are different acts and only one of them is
 * open to a colleague: a refusal leaves a reason on the record.
 */
export function useWithdrawMutation(
  tenantID: string,
  kind: "attestation" | "close" | "scope",
) {
  const qc = useQueryClient();
  const path = (id: string) =>
    kind === "attestation"
      ? `${postureV2Base(tenantID)}/attestation-requests/${encodeURIComponent(id)}/withdraw`
      : kind === "close"
        ? `${postureV2Base(tenantID)}/period/close-requests/${encodeURIComponent(id)}/withdraw`
        : `${postureV2Base(tenantID)}/scope-requests/${encodeURIComponent(id)}/withdraw`;
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      withStepUp(() => apiFetch(path(id), reason ? { method: "POST", body: { reason } } : { method: "POST" })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

export function useDecideCloseMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; approve: boolean; note: string }) =>
      withStepUp(() =>
        apiFetch(
          `${postureV2Base(tenantID)}/period/close-requests/${encodeURIComponent(id)}/decide`,
          { method: "POST", body },
        ),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "close-requests", tenantID] });
      // AND the signed list. Approving IS what creates a close, and the
      // reviewer was told "Accepted and signed. The period is closed." over a
      // panel still reading "No period has been closed yet" until a reload —
      // no packet, no proof. usePeriodClosesQuery deliberately does not poll,
      // because a signed close is immutable; that makes invalidating here the
      // only way the new row appears.
      void qc.invalidateQueries({ queryKey: ["compliance-v2", "period-closes", tenantID] });
    },
  });
}

export function useDecideScopeRequestMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; decision: "approved" | "rejected"; note: string }) =>
      withStepUp(() =>
        apiFetch(`${postureV2Base(tenantID)}/scope-requests/${encodeURIComponent(id)}/decide`, {
          method: "POST",
          body,
        }),
      ),
    onSuccess: () => {
      // The reviewer returns to a framework or control after deciding. Its
      // cached treatment and findings must reflect the accepted decision too.
      void qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

/**
 * useSetExceptionMutation IS GONE, with PUT /exception.
 *
 * The endpoint claimed two-person integrity and did not have it: the
 * approver arrived as a UUID in the request body and the row was written
 * in the requester's single call, so the register recorded that a named
 * colleague approved a failing control without ever asking them.
 *
 * Nothing here called it — the exception dialog has always gone through
 * useRequestScopeExclusionMutation with kind "exception", which opens a
 * request a second person decides from their own session. The mutation
 * sat here as a working direct-write path with no caller, which is how an
 * endpoint grows a caller back.
 */

// ── Framework selection ──────────────────────────────────────────────────

export async function fetchFrameworkSelection(tenantID: string) {
  const raw = await apiFetch(`${postureV2Base(tenantID)}/frameworks/selection`);
  return frameworkSelectionSchema.parse(raw);
}

export function useFrameworkSelectionQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "framework-selection", tenantID],
    queryFn: () => fetchFrameworkSelection(tenantID),
  });
}

export interface FrameworkRef {
  framework_id: string;
  framework_version: string;
}

function sameRef(a: FrameworkRef, b: FrameworkRef): boolean {
  return a.framework_id === b.framework_id && a.framework_version === b.framework_version;
}

/**
 * Serialize scope edits in this client and read the current set before each
 * write. The server compares expected_frameworks under a tenant lock, so an
 * edit from another session cannot silently overwrite this one.
 */
function useChangeFrameworkSelectionMutation<T>(
  tenantID: string,
  compose: (current: FrameworkRef[], input: T) => FrameworkRef[],
) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, T>({
    scope: { id: `compliance-framework-selection:${tenantID}` },
    mutationFn: async (input) => {
      const data = await qc.fetchQuery<FrameworkSelection>({
        queryKey: ["compliance-v2", "framework-selection", tenantID],
        queryFn: () => fetchFrameworkSelection(tenantID),
        staleTime: 0,
      });
      const current = data.selected.map(({ framework_id, framework_version }) => ({
        framework_id, framework_version,
      }));
      return apiFetch(`${postureV2Base(tenantID)}/frameworks/selection`, {
        method: "PUT",
        body: { frameworks: compose(current, input), expected_frameworks: current },
      });
    },
    // Also refresh after conflicts or an ambiguous network failure. Never
    // retry a scope write automatically: the operator reviews the result.
    retry: false,
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ["compliance-v2"] });
    },
  });
}

/** Add a reviewed batch without removing any existing framework. */
export function useAddFrameworksMutation(tenantID: string) {
  return useChangeFrameworkSelectionMutation<FrameworkRef[]>(tenantID, (current, refs) => {
    const next = [...current];
    for (const ref of refs) {
      if (!next.some((f) => sameRef(f, ref))) next.push(ref);
    }
    return next;
  });
}

export function useAddFrameworkMutation(tenantID: string) {
  return useChangeFrameworkSelectionMutation<FrameworkRef>(tenantID, (current, ref) =>
    current.some((f) => sameRef(f, ref)) ? current : [...current, ref],
  );
}

export function useRemoveFrameworkMutation(tenantID: string) {
  return useChangeFrameworkSelectionMutation<FrameworkRef>(tenantID, (current, ref) =>
    current.filter((f) => !sameRef(f, ref)),
  );
}

/**
 * What to tell the operator when a scope change is refused.
 *
 * Two refusals carry a stable code and a `detail` written for a person:
 * "framework-not-offered" (the pack cannot be added today) and
 * "selector-not-eligible" (this principal may not change scope). Their
 * detail is the message. Anything else falls back to the error's own
 * text, and then to the one generic line.
 */
export function scopeErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const body = err.body as Record<string, unknown>;
    const code = typeof body.code === "string" ? body.code : "";
    const detail = typeof body.detail === "string" ? body.detail.trim() : "";
    if ((code === "framework-not-offered" || code === "selector-not-eligible" || code === "scope-conflict") && detail) {
      return detail;
    }
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return "Could not update scope.";
}

export function evidenceArtefactURL(tenantID: string, sha256: string): string {
  return rewritePathForHost(`${postureV2Base(tenantID)}/artefacts/${encodeURIComponent(sha256)}`);
}

export function useEligibleComplianceReviewersQuery(tenantID: string) {
  return useQuery({
    queryKey: ["compliance-v2", "eligible-reviewers", tenantID],
    queryFn: async () => eligibleComplianceReviewersSchema.parse(await apiFetch<unknown>(`${postureV2Base(tenantID)}/reviewers`)),
    staleTime: 60_000,
    enabled: !!tenantID,
  });
}
